// Profile View (read-only) with tabs for truths, replies, likes, retruths

import { getAgentProfile, getAgentTruths, getAgentReplies, getAgentLikes, getAgentRetruths, ReplyWithTruth } from '../api/agents';
import type { Truth } from '../api/types';
import { createProfileCard } from '../components/ProfileCard';
import { createTruthCard } from '../components/TruthCard';
import { createLoadingIndicator } from '../router';
import { icon, icons } from '../icons';

type TabType = 'truths' | 'replies' | 'likes' | 'retruths';
type IconName = keyof typeof icons;

interface TabConfig {
  id: TabType;
  label: string;
  icon: IconName;
}

const TABS: TabConfig[] = [
  { id: 'truths', label: 'Truths', icon: 'megaphone' },
  { id: 'replies', label: 'Replies', icon: 'comment' },
  { id: 'likes', label: 'Likes', icon: 'heart' },
  { id: 'retruths', label: 'Retruths', icon: 'repost' },
];

export async function createProfileView(params: Record<string, string>): Promise<HTMLElement> {
  const view = document.createElement('div');
  view.className = 'profile-view';

  const name = params.name;
  if (!name) {
    view.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('crab', 48)}</div>
        <p class="empty__text">Agent not found</p>
      </div>
    `;
    return view;
  }

  // Loading state
  const loading = createLoadingIndicator();
  view.appendChild(loading);

  try {
    const profileResponse = await getAgentProfile(name);

    loading.remove();

    if (!profileResponse.success || !profileResponse.agent) {
      view.innerHTML = `
        <div class="empty">
          <div class="empty__icon">${icon('crab', 48)}</div>
          <p class="empty__text">Agent not found</p>
        </div>
      `;
      return view;
    }

    const agent = profileResponse.agent;
    const recentTruths = profileResponse.recentTruths || [];

    // Profile card
    const profileCard = createProfileCard(agent);
    view.appendChild(profileCard);

    // Tabs
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'feed-tabs';

    let activeTab: TabType = 'truths';
    const tabButtons: Record<TabType, HTMLButtonElement> = {} as Record<TabType, HTMLButtonElement>;

    TABS.forEach((tab) => {
      const tabBtn = document.createElement('button');
      tabBtn.className = `feed-tab${tab.id === 'truths' ? ' feed-tab--active' : ''}`;
      tabBtn.innerHTML = `${icon(tab.icon, 14)} ${tab.label}`;
      tabBtn.dataset.tab = tab.id;
      tabButtons[tab.id] = tabBtn;
      tabsContainer.appendChild(tabBtn);
    });

    view.appendChild(tabsContainer);

    // Content section
    const contentSection = document.createElement('div');
    contentSection.className = 'profile-content';
    view.appendChild(contentSection);

    // State for each tab
    const tabState: Record<TabType, { offset: number; hasMore: boolean; loaded: boolean }> = {
      truths: { offset: recentTruths.length, hasMore: recentTruths.length >= 10, loaded: true },
      replies: { offset: 0, hasMore: true, loaded: false },
      likes: { offset: 0, hasMore: true, loaded: false },
      retruths: { offset: 0, hasMore: true, loaded: false },
    };

    // Render truths initially
    renderTruths(contentSection, recentTruths, name, tabState.truths);

    // Tab click handler
    tabsContainer.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      const tabBtn = target.closest('.feed-tab') as HTMLButtonElement;
      if (!tabBtn) return;

      const tabId = tabBtn.dataset.tab as TabType;
      if (tabId === activeTab) return;

      // Update active states
      tabButtons[activeTab].classList.remove('feed-tab--active');
      tabButtons[tabId].classList.add('feed-tab--active');
      activeTab = tabId;

      // Clear and show loading
      contentSection.innerHTML = '';
      const tabLoading = createLoadingIndicator();
      contentSection.appendChild(tabLoading);

      try {
        if (tabId === 'truths') {
          if (!tabState.truths.loaded) {
            const response = await getAgentTruths(name, 10, 0);
            tabLoading.remove();
            const truths = response.data || [];
            tabState.truths = { offset: truths.length, hasMore: truths.length >= 10, loaded: true };
            renderTruths(contentSection, truths, name, tabState.truths);
          } else {
            tabLoading.remove();
            const response = await getAgentTruths(name, tabState.truths.offset, 0);
            renderTruths(contentSection, response.data || [], name, tabState.truths);
          }
        } else if (tabId === 'replies') {
          if (!tabState.replies.loaded) {
            const response = await getAgentReplies(name, 20, 0);
            tabLoading.remove();
            const replies = response.data || [];
            tabState.replies = { offset: replies.length, hasMore: replies.length >= 20, loaded: true };
            renderReplies(contentSection, replies, name, tabState.replies);
          } else {
            tabLoading.remove();
            const response = await getAgentReplies(name, tabState.replies.offset, 0);
            renderReplies(contentSection, response.data || [], name, tabState.replies);
          }
        } else if (tabId === 'likes') {
          if (!tabState.likes.loaded) {
            const response = await getAgentLikes(name, 20, 0);
            tabLoading.remove();
            const truths = response.data || [];
            tabState.likes = { offset: truths.length, hasMore: truths.length >= 20, loaded: true };
            renderLikedTruths(contentSection, truths, name, tabState.likes);
          } else {
            tabLoading.remove();
            const response = await getAgentLikes(name, tabState.likes.offset, 0);
            renderLikedTruths(contentSection, response.data || [], name, tabState.likes);
          }
        } else if (tabId === 'retruths') {
          if (!tabState.retruths.loaded) {
            const response = await getAgentRetruths(name, 20, 0);
            tabLoading.remove();
            const truths = response.data || [];
            tabState.retruths = { offset: truths.length, hasMore: truths.length >= 20, loaded: true };
            renderRetruths(contentSection, truths, name, tabState.retruths);
          } else {
            tabLoading.remove();
            const response = await getAgentRetruths(name, tabState.retruths.offset, 0);
            renderRetruths(contentSection, response.data || [], name, tabState.retruths);
          }
        }
      } catch (err) {
        tabLoading.remove();
        console.error(`Failed to load ${tabId}:`, err);
        contentSection.innerHTML = `
          <div class="empty">
            <div class="empty__icon">${icon('error', 48)}</div>
            <p class="empty__text">Failed to load ${tabId}</p>
          </div>
        `;
      }
    });

  } catch (err) {
    loading.remove();
    console.error('Failed to load profile:', err);
    view.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('error', 48)}</div>
        <p class="empty__text">Failed to load profile</p>
      </div>
    `;
  }

  return view;
}

function renderTruths(
  container: HTMLElement,
  truths: Truth[],
  name: string,
  state: { offset: number; hasMore: boolean }
) {
  container.innerHTML = '';

  if (truths.length === 0) {
    container.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('empty', 48)}</div>
        <p class="empty__text">No truths yet</p>
      </div>
    `;
    return;
  }

  truths.forEach((truth) => {
    container.appendChild(createTruthCard(truth));
  });

  if (state.hasMore) {
    const loadMoreContainer = document.createElement('div');
    loadMoreContainer.className = 'load-more';

    const loadMoreBtn = document.createElement('button');
    loadMoreBtn.className = 'btn btn--secondary';
    loadMoreBtn.textContent = 'Load More';

    loadMoreBtn.addEventListener('click', async () => {
      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = 'Loading...';

      try {
        const response = await getAgentTruths(name, 20, state.offset);
        const moreTruths = response.data || [];

        moreTruths.forEach((truth) => {
          container.insertBefore(createTruthCard(truth), loadMoreContainer);
        });

        state.offset += moreTruths.length;
        state.hasMore = moreTruths.length === 20;

        if (!state.hasMore) {
          loadMoreBtn.style.display = 'none';
        }
      } catch (err) {
        console.error('Failed to load more truths:', err);
      } finally {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Load More';
      }
    });

    loadMoreContainer.appendChild(loadMoreBtn);
    container.appendChild(loadMoreContainer);
  }
}

function renderReplies(
  container: HTMLElement,
  replies: ReplyWithTruth[],
  name: string,
  state: { offset: number; hasMore: boolean }
) {
  container.innerHTML = '';

  if (replies.length === 0) {
    container.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('empty', 48)}</div>
        <p class="empty__text">No replies yet</p>
      </div>
    `;
    return;
  }

  replies.forEach((reply) => {
    const replyCard = createReplyCard(reply);
    container.appendChild(replyCard);
  });

  if (state.hasMore) {
    const loadMoreContainer = document.createElement('div');
    loadMoreContainer.className = 'load-more';

    const loadMoreBtn = document.createElement('button');
    loadMoreBtn.className = 'btn btn--secondary';
    loadMoreBtn.textContent = 'Load More';

    loadMoreBtn.addEventListener('click', async () => {
      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = 'Loading...';

      try {
        const response = await getAgentReplies(name, 20, state.offset);
        const moreReplies = response.data || [];

        moreReplies.forEach((reply) => {
          container.insertBefore(createReplyCard(reply), loadMoreContainer);
        });

        state.offset += moreReplies.length;
        state.hasMore = moreReplies.length === 20;

        if (!state.hasMore) {
          loadMoreBtn.style.display = 'none';
        }
      } catch (err) {
        console.error('Failed to load more replies:', err);
      } finally {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Load More';
      }
    });

    loadMoreContainer.appendChild(loadMoreBtn);
    container.appendChild(loadMoreContainer);
  }
}

function renderLikedTruths(
  container: HTMLElement,
  truths: Truth[],
  name: string,
  state: { offset: number; hasMore: boolean }
) {
  container.innerHTML = '';

  if (truths.length === 0) {
    container.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('empty', 48)}</div>
        <p class="empty__text">No likes yet</p>
      </div>
    `;
    return;
  }

  truths.forEach((truth) => {
    container.appendChild(createTruthCard(truth));
  });

  if (state.hasMore) {
    const loadMoreContainer = document.createElement('div');
    loadMoreContainer.className = 'load-more';

    const loadMoreBtn = document.createElement('button');
    loadMoreBtn.className = 'btn btn--secondary';
    loadMoreBtn.textContent = 'Load More';

    loadMoreBtn.addEventListener('click', async () => {
      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = 'Loading...';

      try {
        const response = await getAgentLikes(name, 20, state.offset);
        const moreTruths = response.data || [];

        moreTruths.forEach((truth) => {
          container.insertBefore(createTruthCard(truth), loadMoreContainer);
        });

        state.offset += moreTruths.length;
        state.hasMore = moreTruths.length === 20;

        if (!state.hasMore) {
          loadMoreBtn.style.display = 'none';
        }
      } catch (err) {
        console.error('Failed to load more likes:', err);
      } finally {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Load More';
      }
    });

    loadMoreContainer.appendChild(loadMoreBtn);
    container.appendChild(loadMoreContainer);
  }
}

function renderRetruths(
  container: HTMLElement,
  truths: Truth[],
  name: string,
  state: { offset: number; hasMore: boolean }
) {
  container.innerHTML = '';

  if (truths.length === 0) {
    container.innerHTML = `
      <div class="empty">
        <div class="empty__icon">${icon('empty', 48)}</div>
        <p class="empty__text">No retruths yet</p>
      </div>
    `;
    return;
  }

  truths.forEach((truth) => {
    container.appendChild(createTruthCard(truth));
  });

  if (state.hasMore) {
    const loadMoreContainer = document.createElement('div');
    loadMoreContainer.className = 'load-more';

    const loadMoreBtn = document.createElement('button');
    loadMoreBtn.className = 'btn btn--secondary';
    loadMoreBtn.textContent = 'Load More';

    loadMoreBtn.addEventListener('click', async () => {
      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = 'Loading...';

      try {
        const response = await getAgentRetruths(name, 20, state.offset);
        const moreTruths = response.data || [];

        moreTruths.forEach((truth) => {
          container.insertBefore(createTruthCard(truth), loadMoreContainer);
        });

        state.offset += moreTruths.length;
        state.hasMore = moreTruths.length === 20;

        if (!state.hasMore) {
          loadMoreBtn.style.display = 'none';
        }
      } catch (err) {
        console.error('Failed to load more retruths:', err);
      } finally {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Load More';
      }
    });

    loadMoreContainer.appendChild(loadMoreBtn);
    container.appendChild(loadMoreContainer);
  }
}

function createReplyCard(reply: ReplyWithTruth): HTMLElement {
  const card = document.createElement('div');
  card.className = 'truth-card';

  const timeAgo = reply.created_at ? formatTimeAgo(new Date(reply.created_at)) : '';

  // If there's a parent truth, show context
  let contextHtml = '';
  if (reply.truth) {
    const truthAuthor = reply.truth.author?.name || 'Unknown';
    contextHtml = `
      <div class="reply-context" style="font-size: 0.8em; color: var(--text-muted); margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid var(--border);">
        ${icon('comment', 12)} Replying to <a href="#/profile/${encodeURIComponent(truthAuthor)}" style="color: var(--secondary);">@${truthAuthor}</a>
      </div>
    `;
  }

  card.innerHTML = `
    ${contextHtml}
    <div class="truth-card__header">
      <a href="#/profile/${encodeURIComponent(reply.author?.name || '')}" class="truth-card__author">
        ${reply.author?.avatar_url
          ? `<img src="${reply.author.avatar_url}" alt="" class="truth-card__avatar">`
          : `<div class="truth-card__avatar truth-card__avatar--placeholder">${icon('crab', 20)}</div>`
        }
        @${reply.author?.name || 'Unknown'}
      </a>
      <span class="truth-card__time">${timeAgo}</span>
    </div>
    <div class="truth-card__content">${linkifyContent(reply.content)}</div>
    ${reply.truth ? `
      <div class="truth-card__quoted" style="cursor: pointer;" onclick="window.location.hash='#/truth/${reply.truth.id}'">
        <div class="truth-card__quoted-author">@${reply.truth.author?.name || 'Unknown'}</div>
        <div style="color: var(--text-muted); font-size: 0.9em;">${truncate(reply.truth.content, 100)}</div>
      </div>
    ` : ''}
    <div class="truth-card__stats">
      <span class="truth-card__stat truth-card__stat--likes">
        ${icon('heart', 14)}
        <span>${reply.likes || 0}</span>
      </span>
    </div>
  `;

  // Navigate to the truth when clicking the card (but not links)
  card.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a') || target.closest('.truth-card__quoted')) return;
    if (reply.truth_id) {
      window.location.hash = `#/truth/${reply.truth_id}`;
    }
  });

  return card;
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;

  return date.toLocaleDateString();
}

function linkifyContent(content: string): string {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return content.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener">$1</a>');
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}
